import { Fragment, useCallback, ReactNode } from 'react'
import classNames from 'classnames/bind'

import { Td, TrSelectable, TdLabel } from 'app/common/ui-components/table'
import { type Settings } from 'app/reducers/settings/dishes'
import PlainCheckbox from 'app/common/ui-next/plain-checkbox'
import { useSelectionData } from 'app/components/common/SelectionData'
import { useSaleTypesAsArray } from 'app/dataProviders/SaleTypesData'
import { CellContentSaleType } from 'app/components/common/cellContents/CellContentSaleType'
import { CellContentPhoto } from 'app/components/common/cellContents/CellContentPhoto'

import DishesTableOptions from './DishesTableOptions'
import DishesTableEditableProp from './DishesTableEditableProp'
import { useDishesData, SaveType, Photo } from '../../../../dataProviders/dishes/DishesData'

import styles from './DishesTableRow.scss'

/**
 *  Строка в таблице блюд
 */

const cx = classNames.bind(styles)

type TitleTdProps = { children: ReactNode, htmlFor?: string, className?: string }
const TitleTd = ({ children, htmlFor, className }: TitleTdProps) => (
  htmlFor
    ? <TdLabel htmlFor={htmlFor} className={className}>{children}</TdLabel>
    : <Td className={className}>{children}</Td>
)

type Props = {
  id: string,
  title: string,
  photoUrl: string | null,
  cost: string,
  portion: string,
  saleType: {
    id: string
    title: string
  },
  settings: Settings,
  loading: boolean,
  locked?: boolean,
  nomenclatureActive: boolean,
  nomenclature: string | null,
}

const DishesTableRow = ({
  id,
  title,
  photoUrl,
  portion,
  cost,
  saleType,
  settings,
  loading,
  locked,
  nomenclatureActive,
  nomenclature,
}: Props) => {
  const {
    check: showCheck,
    photo: showPhoto,
    portion: showPortion,
    cost: showCost,
    saleType: showSaleType,
    options: showOptions,
    nomenclature: showNomenclature,
    id: showId,
  } = settings

  // TODO replace by import { CheckStateProvider } from 'app/common/ui-components/table'
  const [{ selection }, { update: selectionUpdate }] = useSelectionData()
  const selected = selection[String(id)]
  const [, { fieldUpdate, photoDelete, photoUpdate }] = useDishesData()

  const { saleTypes, loading: loadingSaleTypes } = useSaleTypesAsArray()

  const handleClick = useCallback((_, checked) => {
    selectionUpdate(id, checked)
  }, [selectionUpdate, id])

  const makeHandleSubmitProp = useCallback((propName: string) => {
    return (value: string, options: SaveType) => {
      return fieldUpdate({
        valueType: 'common',
        id,
        field: propName,
        value,
        changeItemTypes: options,
      })
    }
  }, [fieldUpdate, id])

  const handlePhotoDelete = useCallback(() => {
    return photoDelete(id)
  }, [photoDelete, id])

  const handlePhotoAdd = useCallback((photoParams: Photo) => {
    return photoUpdate(id, photoParams)
  }, [photoUpdate, id])

  const handleChangeSaleType = useCallback((saleTypeId: string) => {
    return fieldUpdate({
      valueType: 'common',
      id,
      field: 'sale_type_id',
      value: saleTypeId,
    })
  }, [fieldUpdate, id])

  const rowId = `row_${id}`
  const titleProps = showCheck ? { htmlFor: rowId } : {}

  return (
    <TrSelectable
      key={id}
      id={rowId}
      selected={selected}
      disabled={loading}
      onClick={handleClick}
      className={cx(styles.root, { selected })}
    >
      {checkboxProps => (
        <Fragment>
          {showCheck
            && <TdLabel
              htmlFor={rowId}
              className={styles.columnCheckbox}
            >
              <PlainCheckbox
                label={title}
                skipLabel
                {...checkboxProps}
              />
            </TdLabel>}

          {showId
            && nomenclatureActive
            && <TitleTd {...titleProps}>
              {id}
            </TitleTd>}

          {showNomenclature
            && nomenclatureActive
            && <TitleTd {...titleProps}>
              {nomenclature}
            </TitleTd>}

          <TitleTd {...titleProps} className={styles.title}>
            {title}
          </TitleTd>

          {showPhoto
            && <Td alignCenter>
              <CellContentPhoto
                photoUrl={photoUrl || ''}
                deleteConfirmText={`Вы уверены? Удалить изображение из блюда "${title}"?`}
                disabled={locked}
                onSaveImage={handlePhotoAdd}
                onDeleteImage={handlePhotoDelete}
              />
            </Td>}

          {showPortion
            && <Td>
              {locked
                ? portion
                : <DishesTableEditableProp
                  value={portion}
                  onSubmit={makeHandleSubmitProp('portion')}
                />}
            </Td>}

          {showCost
            && <Td>
              {locked
                ? cost
                : <DishesTableEditableProp
                  value={cost}
                  onSubmit={makeHandleSubmitProp('cost')}
                  floatType
                />}
            </Td>}

          {showSaleType
            && <Td
              className={styles.columnSaleTypes}
            >
              <CellContentSaleType
                id={id}
                currentSaleTypeId={saleType.id}
                pending={loadingSaleTypes}
                saleTypes={saleTypes}
                locked={locked}
                onChangeSaleType={handleChangeSaleType}
              />
            </Td>}

          {showOptions
            && <Td
              alignRight
            >
              {!locked
                && <DishesTableOptions
                  id={parseInt(id, 10)}
                  title={title}
                />}
            </Td>}
        </Fragment>
      )}
    </TrSelectable>
  )
}

export default DishesTableRow
